/** @jsxImportSource theme-ui */
import { Flex, Box, Card, Spinner, Text } from 'theme-ui';
import { Formik } from 'formik';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import { TrashIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';
import { GrayFormikField } from '@/components/GrayFormikField';
import Button from '@/components/Button';
import {
    useDeleteTicketCommentMutation,
    useGetTicketCommentsQuery,
    usePostTicketCommentMutation,
} from '@/store/apiService';
import { TicketComment } from '@/types/types';

interface Props {
    ticketId: number;
    initialComments?: TicketComment[];
}

const formatDate = (value?: string) => {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : format(d, 'd MMM yyyy, p');
};

const TicketComments: React.FC<Props> = ({ ticketId, initialComments }) => {
    const commentsQuery = useGetTicketCommentsQuery({ SupportCaseId: ticketId });
    const [postComment, postState] = usePostTicketCommentMutation();
    const [deleteComment, deleteState] = useDeleteTicketCommentMutation();

    const comments = commentsQuery.data?.Results ?? initialComments ?? [];

    return (
        <Flex sx={{ gap: 2, flexDirection: 'column' }}>
            <Box variant="segment">
                <Formik
                    initialValues={{ Comment: '' }}
                    onSubmit={(values, { resetForm }) => {
                        if (!values.Comment?.trim()) return;
                        postComment({ Id: ticketId, Comment: values.Comment })
                            .unwrap()
                            .then(() => resetForm())
                            .catch((err) =>
                                toast.error(err?.data ?? err?.message ?? 'Add comment failed', {
                                    autoClose: 5000,
                                })
                            );
                    }}
                >
                    {(props) => (
                        <Card variant="widget">
                            <Flex sx={{ gap: 2, flexDirection: 'column', alignItems: 'flex-end' }}>
                                <GrayFormikField
                                    name="Comment"
                                    type="textarea"
                                    label="Add comment"
                                />
                                <Button
                                    isLoading={postState.isLoading}
                                    disabled={postState.isLoading || !props.values.Comment?.trim()}
                                    type="submit"
                                    onClick={props.submitForm}
                                >
                                    Post comment
                                </Button>
                            </Flex>
                        </Card>
                    )}
                </Formik>
            </Box>
            <Box variant="segment">
                {commentsQuery.isLoading ? <Spinner size={16} /> : null}
                {!commentsQuery.isLoading && comments.length === 0 ? (
                    <Text>No comments yet</Text>
                ) : null}
                <Flex sx={{ gap: 2, flexDirection: 'column' }}>
                    {comments.map((comment) => (
                        <Card key={comment.Id} variant="widget">
                            <Flex sx={{ alignItems: 'center', mb: 2 }}>
                                <Text sx={{ flexGrow: 1 }}>{formatDate(comment.Created)}</Text>
                                <Text>{comment.CreatedBy}</Text>
                            </Flex>
                            <Flex sx={{ alignItems: 'center' }}>
                                <Box
                                    sx={{ flexGrow: 1 }}
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment.Comment ?? '') }}
                                />
                                <Button
                                    isLoading={
                                        deleteState?.originalArgs?.Id === comment.Id &&
                                        deleteState.isLoading
                                    }
                                    variant="dangerOutline"
                                    onClick={() => {
                                        if (comment.Id == null) return;
                                        deleteComment({
                                            SupportCaseId: ticketId,
                                            Id: comment.Id,
                                        })
                                            .unwrap()
                                            .catch((err) =>
                                                toast.error(
                                                    err?.data ?? err?.message ?? 'Delete failed',
                                                    { autoClose: 5000 }
                                                )
                                            );
                                    }}
                                >
                                    <TrashIcon width={16} height={16} />
                                </Button>
                            </Flex>
                        </Card>
                    ))}
                </Flex>
            </Box>
        </Flex>
    );
};

export default TicketComments;
